import wsStorage from "lib/wsStorage";
import React from "react";
import PropTypes from "prop-types";
import Slider from "react-slick";
import classNames from "classnames";

import SVGIcon from "common/SVGIcon";
import { applicationShape, questionShape } from "lib/propTypes";

import JobPromoCard from "./AlertCard/HomeAlertCardJobPromo";
import FinishCard from "./AlertCard/HomeAlertCardFinish";

const PROMO_KEY = "home-job-promo";
const PROMO_MAX_TIMES_SHOWN = 3;

/* eslint-disable react/prop-types */
// react-slick passes a bunch of props by default to whatever
// you pass to nextArrow and prevArrow, so you have to impl a
// custom class to "ignore" them so they don't try and get
// passed to DOM nodes and generate React errors
const ArrowIcon = ({ slideCount, currentSlide, ...props }) => <SVGIcon {...props} />;
/* eslint-enable */

export default class AlertCards extends React.PureComponent {
    static propTypes = {
        invites: PropTypes.arrayOf(applicationShape).isRequired,
        expired: PropTypes.arrayOf(applicationShape).isRequired,
        incomplete: PropTypes.arrayOf(applicationShape).isRequired,
        displayedAlerts: PropTypes.number,
        gender: PropTypes.string.isRequired,
        handleShowMatchScoreModal: PropTypes.func.isRequired,
        hasJobAlerts: PropTypes.bool.isRequired,
        hasJobRefs: PropTypes.bool.isRequired,
        hasPrefs: PropTypes.bool.isRequired,
        questions: PropTypes.arrayOf(questionShape),
        subscriptionsURL: PropTypes.string.isRequired,
    };

    static defaultProps = {
        displayedAlerts: 8,
        questions: [],
    };

    renderInvites(invites) {
        if (invites.length === 0) {
            return null;
        }

        const inviteCardGender =
            this.props.gender === "male"
                ? "cand-home__invite-card-male"
                : "cand-home__invite-card-female";
        const inviteCardStyle = classNames("cand-home__invite-card", inviteCardGender);
        return invites.map((app) => (
            <div className="slider-card slider-card--home" key={app.position.id}>
                <div className={inviteCardStyle} />
                <p>You&apos;re invited to {app.position.interview_type.long}!</p>
                <span>
                    {app.position.title} - {app.position.company_name}
                </span>
                <a href={app.position.link} className="secondary-text-link">
                    See job details
                </a>
                <div className="cand-home__invite-options">
                    <a href={app.withdraw_url} className="secondary-text-link">
                        Not Interested
                    </a>
                    <a href={app.schedule_url} className="">
                        Select a time
                    </a>
                </div>
            </div>
        ));
    }

    renderExpirations(expired) {
        if (expired.length === 0) {
            return null;
        }

        const expiredCardGender =
            this.props.gender === "male"
                ? "cand-home__expired-card-male"
                : "cand-home__expired-card-female";
        const expiredCardStyle = classNames("cand-home__expired-card", expiredCardGender);
        return expired.map((app) => (
            <div className="slider-card slider-card--home" key={app.position.id}>
                <div className={expiredCardStyle} />
                <p>Your application expired</p>
                <span>
                    {app.position.title} - {app.position.company_name}
                </span>
                <a href={`${app.position.link}?history=1`} className="secondary-text-link">
                    See job details
                </a>
                <div className="cand-home__expired-options">
                    <a href="/app/reapply">Are you still Interested?</a>
                </div>
            </div>
        ));
    }

    renderIncomplete(incomplete) {
        if (incomplete.length === 0) {
            return null;
        }

        return <FinishCard apps={incomplete} progress={90} maxList={1} />;
    }

    renderJobAlertPromo() {
        const { hasJobAlerts, subscriptionsURL } = this.props;
        if (hasJobAlerts) {
            return null;
        }

        // In an ideal world, all this logic would be encapsulated in the external
        // component, but the way Slick's Slider works is that it is incapable of
        // filtering out "null" components from its final render (as opposed to
        // a function that literally returns null like these do), so it ends up
        // rendering an "empty" spot where a card should be. Cool.
        const timesShown = wsStorage.getItem(PROMO_KEY) || 0;

        if (timesShown >= PROMO_MAX_TIMES_SHOWN) {
            return null;
        }

        wsStorage.setItem(PROMO_KEY, timesShown + 1, 2592000 * 1000); // 30 days

        return <JobPromoCard subscriptionsURL={subscriptionsURL} />;
    }

    renderPrefPrompt() {
        if (this.props.hasPrefs) {
            return null;
        }

        return (
            <div className="slider-card slider-card--home">
                <div className="cand-home__prefs-card" />
                <p>Where are you looking?</p>
                <span>Set job preferences</span>
                <div className="cand-home__pref-options">
                    <a href="#" data-reload="set-preferences" className="js-edit-preferences">
                        Set max commute
                    </a>
                </div>
            </div>
        );
    }

    renderQuestion() {
        if (this.props.questions.length === 0) {
            return null;
        }

        return this.props.questions.map((q) => (
            <div key={q.id} className="slider-card slider-card--home slider-card--question">
                <aside>
                    Improve your{" "}
                    <a
                        href="#"
                        onClick={this.props.handleShowMatchScoreModal}
                        data-source="alert-card"
                    >
                        Match Score
                    </a>
                </aside>
                <header>{q.question}</header>
                <a href={`/u/questions/${q.hash || q.id}/`}>Answer</a>
            </div>
        ));
    }

    renderReference() {
        if (this.props.hasJobRefs) {
            return null;
        }

        return (
            <div className="slider-card slider-card--home cand-home__ref-card">
                <div className="cand-home__invite-card cand-home__ref-card-header" />
                <p>Get a professional recommendation</p>
                <span>Stand out with a recommendation from a former manager supervisor</span>
                <div className="cand-home__ref-options">
                    <a href="/profile/#references">Request recommendation</a>
                </div>
            </div>
        );
    }

    render() {
        const invites = this.props.invites.slice(0, this.props.displayedAlerts);
        const expired = this.props.expired.slice(0, this.props.displayedAlerts - invites.length);
        const prefsCard = this.props.hasPrefs ? 0 : 1;
        const refCard = this.props.hasJobRefs ? 0 : 1;
        const actionCount =
            invites.length +
            expired.length +
            prefsCard +
            refCard +
            this.props.questions.length +
            this.props.incomplete.length;

        const show3Cards = actionCount >= 3 ? 3 : actionCount;
        const show2Cards = actionCount >= 2 ? 2 : actionCount;
        const sliderSettings = {
            dots: true,
            infinite: false,
            speed: 500,
            slidesToShow: show3Cards,
            slidesToScroll: 1,
            initialSlide: 0,
            nextArrow: <ArrowIcon icon="chevron-right" />,
            prevArrow: <ArrowIcon icon="chevron-left" />,
            responsive: [
                {
                    breakpoint: 1200,
                    settings: {
                        slidesToShow: show2Cards,
                    },
                },
                {
                    breakpoint: 840,
                    settings: {
                        slidesToShow: 2,
                        arrows: false,
                    },
                },
                {
                    breakpoint: 768,
                    settings: {
                        centerMode: true,
                        centerPadding: "5px",
                        variableWidth: true,
                        slidesToShow: 1,
                        arrows: false,
                    },
                },
            ],
        };

        return (
            <section className="cand-home__alert-bar">
                <Slider {...sliderSettings} className="slider">
                    {this.renderInvites(invites)}
                    {this.renderIncomplete(this.props.incomplete)}
                    {this.renderJobAlertPromo()}
                    {this.renderExpirations(expired)}
                    {this.renderPrefPrompt()}
                    {this.renderQuestion()}
                    {/* this.renderReference() */}
                </Slider>
            </section>
        );
    }
}
